import { useCallback, useEffect, useState } from "react";
import { Card } from "@pericles/framework-frontend";

import { fetchDiscoveryNotes, promoteDiscoveryMessageToNote } from "../api/discoveryChatClient";
import type { DiscoveryNote } from "../types/discoveryChat";

import { InlineAlert } from "./InlineAlert";

interface ProjectDiscoveryNotesPanelProps {
  projectId: string;
  refreshKey?: number;
  onNotesChange?: (notes: DiscoveryNote[]) => void;
}

export function ProjectDiscoveryNotesPanel({
  projectId,
  refreshKey,
  onNotesChange,
}: ProjectDiscoveryNotesPanelProps): JSX.Element {
  const [notes, setNotes] = useState<DiscoveryNote[]>([]);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [isLoadingNotes, setIsLoadingNotes] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const loadNotes = useCallback(async () => {
    if (!projectId) return;

    setIsLoadingNotes(true);
    setNotesError(null);

    try {
      const fetchedNotes = await fetchDiscoveryNotes(projectId);
      setNotes(fetchedNotes);
      onNotesChange?.(fetchedNotes);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load notes";
      setNotesError(message);
    } finally {
      setIsLoadingNotes(false);
    }
  }, [projectId, onNotesChange]);

  useEffect(() => {
    setNotes([]);
    void loadNotes();
  }, [loadNotes, projectId, refreshKey]);

  const handleCreateNote = async () => {
    if (!projectId || !newNote.trim()) return;

    setIsSaving(true);
    setNotesError(null);

    try {
      await promoteDiscoveryMessageToNote(projectId, newNote.trim());
      setNewNote("");
      await loadNotes();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create note";
      setNotesError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card title="Notes Discovery">
      <div className="space-y-4 p-4 text-sm text-slate-700">
        <p>Consignez les points clés de la Discovery. Les notes promues depuis les conversations apparaissent ici.</p>

        <div aria-live="polite" className="space-y-2">
          {notesError ? <InlineAlert tone="danger" title={notesError} /> : null}
          {isLoadingNotes ? <p>Chargement des notes…</p> : null}
        </div>

        <div className="space-y-2">
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            <span>Ajouter une note</span>
            <textarea
              className="input min-h-[96px]"
              placeholder="Résumé d'un insight, risque, contrainte ou décision"
              value={newNote}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setNewNote(event.target.value)}
              disabled={isSaving}
            />
          </label>

          <button
            type="button"
            className="button button-primary"
            onClick={() => void handleCreateNote()}
            disabled={isSaving || !newNote.trim()}
          >
            {isSaving ? "Enregistrement…" : "Enregistrer la note"}
          </button>
        </div>

        {!isLoadingNotes && !notesError && notes.length === 0 ? (
          <p className="text-slate-600">Les notes promues depuis les conversations apparaîtront ici.</p>
        ) : null}

        {!isLoadingNotes && notes.length > 0 ? (
          <ul className="space-y-2">
            {notes.map((note) => (
              <li key={note.id} className="rounded border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Note créée</span>
                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                </div>
                <div className="mt-1 whitespace-pre-line text-slate-800">{note.content}</div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
